<script lang="ts" setup>
import { computed, ref, unref, watch } from "vue";
import { storeToRefs } from "pinia";
import { PRadioButton, PTooltip } from "@profeat/ui-kit";

import { useContacts } from "~/stores/contacts.store";
import { useBroadcast } from "~/stores/broadcast.store";
import { useChatbots } from "~/stores/chatbots.store";
import { BroadcastDelay } from "../broadcastV2/types/broadcast-delay";
import WorkingDelayPicker from "../broadcastV2/widgets/WorkingDelayPicker.vue";

const contactsStore = useContacts();
const broadcastStore = useBroadcast();

const withDelayRadioValue = computed({
  get() {
    return Number(withDelay.value);
  },
  set(value: number) {
    withDelay.value = !!value;
  },
});
const withDelay = ref(false);
const workingDays = ref<string[]>(["Пн", "Вт", "Ср", "Чт", "Пт"]);
const workingDaysTimeInclude = ref<string[]>(["08:00-20:00"]);
const weekendDaysTimeInclude = ref<string[]>([]);

const timezone = ref<number>(-(new Date().getTimezoneOffset() / 60));
const workingDelay = computed<BroadcastDelay>(() => {
  return {
    workingDays: unref(workingDays),
    workingDaysTimeInclude: unref(workingDaysTimeInclude),
    weekendDaysTimeInclude: unref(weekendDaysTimeInclude),
    timezone: unref(timezone),
  };
});

const { getSelectionsCount, getEmptyContactsCount } = storeToRefs(contactsStore);

const chatbotsStore = useChatbots();

const selectedChatBot = ref("-1");

const chatBotsOptions = ref<{ text: string; value: string }[]>(
  chatbotsStore.templateBotSelectOptions
);

const updateSelectedBotId = async (chatBotId: string) => {
  broadcastStore.selectedChatBotId = chatBotId === "-1" ? "" : chatBotId;
};

watch(
  () => workingDelay.value,
  (value) => broadcastStore.updateDelay(value)
);

watch(
  () => withDelay.value,
  (is) => broadcastStore.updateDelay(is ? workingDelay.value : null)
);
</script>

<template>
  <div class="wrapper">
    <div class="main">
      <div class="info">
        <p>
          {{ $t("broadcast.contactsCount") }}:
          {{ contactsStore.getSelectionsCount }} {{ $t("customers.of") }}
          {{ getSelectionsCount + getEmptyContactsCount }}
        </p>
        <PTooltip placement="right-start">
          <!-- @vue-ignore -->
          <template #activator="{ setReference, click }">
            <svg
              :ref="setReference"
              @click="click"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M10 3.5C6.41015 3.5 3.5 6.41015 3.5 10C3.5 13.5899 6.41015 16.5 10 16.5C13.5899 16.5 16.5 13.5899 16.5 10C16.5 6.41015 13.5899 3.5 10 3.5ZM1.5 10C1.5 5.30558 5.30558 1.5 10 1.5C14.6944 1.5 18.5 5.30558 18.5 10C18.5 14.6944 14.6944 18.5 10 18.5C5.30558 18.5 1.5 14.6944 1.5 10ZM9 6.66666C9 6.11437 9.44772 5.66666 10 5.66666H10.0083C10.5606 5.66666 11.0083 6.11437 11.0083 6.66666C11.0083 7.21894 10.5606 7.66666 10.0083 7.66666H10C9.44772 7.66666 9 7.21894 9 6.66666ZM9.16663 9C8.61434 9 8.16663 9.44772 8.16663 10C8.16663 10.4955 8.52701 10.9068 8.99996 10.9862V13.3333C8.99996 13.8856 9.44767 14.3333 9.99996 14.3333H10.8333C11.3856 14.3333 11.8333 13.8856 11.8333 13.3333C11.8333 12.8378 11.4729 12.4265 11 12.3472V10C11 9.44772 10.5522 9 9.99996 9H9.16663Z"
                fill="#B6C7D6"
              />
            </svg>
          </template>
          <template #text>
            <div class="p-popover__content">
              {{ $t("broadcast.info.description") }}
              <ol>
                <li>{{ $t("broadcast.info.list.empty") }}</li>
                <li :class="{'error-hint': getEmptyContactsCount}">{{ $t("broadcast.info.list.emptyPhone") }}</li>
                <li>{{ $t("broadcast.info.list.titleMax") }}</li>
                <li>{{ $t("broadcast.info.list.contentMax") }}</li>
              </ol>
            </div>
          </template>
        </PTooltip>
      </div>
      <div class="w-full flex flex-col gap-[16px] delay">
        <div class="flex items-center gap-[16px]">
          <div class="checkbox">
            <div class="checkbox-container">
              <PRadioButton
                v-model="withDelayRadioValue"
                :label="$t('broadcast.delay.noDelay')"
                :label-position="'right'"
                :disabled="false"
                :text-size="'md'"
                :value="0"
              />
            </div>
            <div class="checkbox-container">
              <PRadioButton
                v-model="withDelayRadioValue"
                :label="$t('broadcast.delay.withDelay')"
                :label-position="'right'"
                :disabled="false"
                :text-size="'md'"
                :value="1"
              />
            </div>
          </div>
        </div>
        <div v-if="withDelay" class="w-full flex flex-col gap-[24px]">
          <div class="delay-info">
            {{ $t("broadcast.delay.info") }}
          </div>
          <WorkingDelayPicker
            v-model:workingDays="workingDays"
            v-model:workingDaysTimeInclude="workingDaysTimeInclude"
            v-model:weekendDaysTimeInclude="weekendDaysTimeInclude"
            v-model:timezone="timezone"
          />
        </div>
      </div>
      <div class="bot">
        <p class="bot__label">{{ $t("broadcast.autoHandler") }}</p>
        <!-- @vue-ignore -->
        <PSelect
          v-model="selectedChatBot"
          class="bot__select"
          @update:model-value="updateSelectedBotId"
          :placeholder="$t('triggerBroadcast.sample.chatbot.notSelected')"
          :options="chatBotsOptions"
          :label="'Чат-бот'"
          :max-height="250"
          size="large"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  height: 100%;
}

.info {
  display: flex;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: var(--common-black);
  align-items: center;
  gap: 4px;
}

.main {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.info .p-tooltip-desktop {
  height: 20px;
}

.bot__label {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: var(--common-black);
  margin-bottom: 16px;
}

.bot__select {
  width: 100%;
  max-width: 360px;
}

.p-popover__content {
  display: flex;
  flex-direction: column;
  max-width: 264px;
  gap: 20px;
  font-weight: normal;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.error-hint {
  color: var(--ui-error);
}

.delay {
  &-info {
    @include regular-14;
    color: var(--common-asphalt);
  }
}
</style>
